import { Link } from "react-router-dom";
import { AskForAccess } from "../components/AskForAccess.js";
import { Lockup } from "../components/Logo.js";

/**
 * The landing page (1a).
 *
 * Minimal on purpose: the front door for an owner, a line for a visitor who
 * has an invitation and lost the email, and a form for a stranger who wants
 * one. It is also the only page a person can arrive at without being invited,
 * which makes the promises on it the first thing the product says about
 * itself.
 *
 * The wording of the confirmation matters as much as the endpoint behind it -
 * see the enumeration rule in docs/product-model.md. "If they use Meet With"
 * is the whole point: it is true, it is the same sentence for everyone, and it
 * tells a stranger nothing they did not already know.
 */
export function Landing() {
  return (
    <main className="landing">
      <header className="landing-bar">
        <Lockup size={22} />
        <Link className="ghost" to="/sign-in">
          Sign in
        </Link>
      </header>

      <section className="landing-hero">
        <h1>One free/busy picture across every work calendar you have.</h1>
        <p className="lede">
          Connect the Microsoft 365 and Google accounts you actually use — across
          different organisations — and the people you invite see one honest view of
          when you&rsquo;re free.
        </p>
        <div className="row">
          <Link className="primary" to="/sign-in">
            Connect your calendars
          </Link>
          <span className="helper">Free while it&rsquo;s in the making.</span>
        </div>
      </section>

      <section className="promise">
        <h2>What makes this different</h2>
        <ul>
          <li>
            <strong>Invite-only.</strong> There is no public link to your availability,
            at any price. You name each address, and revoking one takes effect
            immediately.
          </li>
          <li>
            <strong>Read-only.</strong> Meet With never creates, moves or deletes an
            event, and never sends mail from your mailbox. Your IT reviewer can check
            that in the consent screen.
          </li>
          <li>
            <strong>Nothing is booked without you.</strong> A visitor requests; you
            accept, suggest another time, or decline.
          </li>
        </ul>
      </section>

      <section className="landing-split">
        <div className="landing-panel">
          <h2>Got an invitation?</h2>
          <p className="helper">
            You don&rsquo;t sign in here — there is no account to make. Open the link in
            the email from Meet With and confirm the code it sends you. If you can&rsquo;t
            find it, check spam, then ask whoever invited you to send it again.
          </p>
        </div>

        <div className="landing-panel">
          <h2>Want one?</h2>
          <AskForAccess />
        </div>
      </section>

      <footer className="landing-foot">
        <span>Invite-only · Read-only · Nothing booked without your approval</span>
        <span>
          <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link>
        </span>
      </footer>
    </main>
  );
}
